import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from './Icon.js';

/**
 * Spec 235 — the queue row.
 *
 * `ResponseCard` today is a single `<Link>` wrapping the entire card — see
 * `client/src/components/ResponseCard.tsx`. That shape has two problems this
 * recipe exists to fix, not just restyle around:
 *
 *   (a) It is invalid HTML the moment a row needs its own nested control
 *       (an approve button, a "view domain" link). Anchors cannot nest —
 *       browsers auto-close/reparent an inner `<a>`, silently breaking it
 *       rather than throwing — and a `<button>` inside a `<Link>` at least
 *       depends on `stopPropagation()` being remembered on every call site.
 *   (b) It gives the link an accessible name that is the card's ENTIRE text
 *       content concatenated (engine badge + timestamp + prompt + snippet +
 *       every quick metric, run together), because the whole subtree is
 *       inside the one `<a>`. A screen reader announces all of that as "the
 *       link", not "the prompt", which is not a name at all.
 *
 * The "stretched link" pattern fixes both at once: `Row` renders exactly one
 * real `<a>` (via `<Link>`), wrapping only `title`. CSS
 * (`.hub-row__link::after { position: absolute; inset: 0 }`, primitives.css)
 * stretches that anchor's hit area to the row's full box, so clicking or
 * hovering anywhere in the row still activates it. `actions` (and anything
 * wrapped in `.hub-row__interactive`) are DOM **siblings** of that anchor,
 * never descendants, so a click on a nested button never bubbles through a
 * row-link ancestor — there is nothing to `stopPropagation()` against, only
 * a z-index (`.hub-row__actions`) that has to out-rank the anchor's `::after`.
 * Do not re-add an `onClick` on `.hub-row`/`<li>` "for safety" — that is the
 * div-with-onClick anti-pattern re-entering through the back door, and it
 * reintroduces exactly the bubbling problem this pattern removes.
 *
 * Text selection is unchanged by any of this: a full-card link already
 * blocks normal text selection today, so shrinking the anchor down to the
 * title does not take away a capability users currently have.
 *
 * Keyboard focus: `.hub-row__link:focus-visible` suppresses the browser's
 * default ring on the anchor's own (title-sized) box and redraws it on
 * `::after` instead, so the ring keyboard users see wraps the whole row —
 * still the same DOM node receiving focus, only the *painted* outline moves.
 *
 * Split into a small family — `RowList` (the `<ul>`), `Row` (the `<li>` that
 * owns the link), and plain slot components (`RowSnippet`, `RowFacts`/
 * `RowFact`, `RowSources`) — mirroring the existing `Region`/`RegionHead` and
 * `StatRow`/`Stat` convention: a container, a shell that carries the hard
 * behaviour, and markup+class slots. Some queues use four of these parts,
 * one uses all seven; a single mega-component with fourteen boolean props
 * would hide exactly the composition this problem needs.
 *
 * Spec 235 §3.3 — a row that goes nowhere is still a row. The first version
 * of this file declared `to: string` as required, which could only express
 * one of the thirteen pages' three list shapes: the **navigable queue**
 * (`ResponseCard`, `PlaybooksPage` — the row opens a detail page). It broke
 * on the **disclosure list** (`PersonaEvaluationsPage`'s `PersonaFindingCard`:
 * `aria-expanded` accordions, zero links, four independent in-place toggles).
 * Requiring `to` for that shape would have meant either fabricating a
 * destination that does not exist, or wrapping an already-correct disclosure
 * component in a stretched anchor it does not need and whose accessible-name
 * and nested-control machinery it did not ask for.
 *
 * Kridt's actual rule was never about navigation — it is about the
 * CONTAINER: hairlines instead of a card, one padding, one slot structure.
 * So `to` is optional. Give it, and `Row` behaves exactly as documented
 * above: one stretched `<a>`, nested actions lifted above its `::after`.
 * Omit it, and `Row` renders the same `<li>`/lead/top/body/actions shape
 * with NO anchor and NO `::after` overlay — a plain container. Its own
 * interactive content (an accordion toggle passed through `children`) needs
 * no `z-index` lift and no `stopPropagation`, for the same reason a button
 * on an ordinary page never does: there is nothing stretched over it to
 * out-rank. The two variants share their CSS byte-for-byte (primitives.css
 * has no `:has(.hub-row__link…)`-only branch in the box model, only in the
 * hover/focus paint) — one container rule, two interaction models, which is
 * the point: a disclosure row that never gets a destination should still
 * never look like a card in a tray again just because it doesn't navigate.
 */
export function RowList({ children, className, ...rest }: { children: ReactNode; className?: string } & Record<string, unknown>) {
  return (
    <ul className={`hub-rows${className ? ' ' + className : ''}`} {...rest}>
      {children}
    </ul>
  );
}

export interface RowProps {
  /** Destination — the row's one real link (react-router `<Link>`). Omit it
   *  for a DISCLOSURE row (spec 235 §3.3): one that expands in place rather
   *  than navigating. With no `to`, `Row` renders no anchor and no stretched
   *  `::after`; the row is a plain container and whatever interactive
   *  control lives in `children` (an accordion toggle, say) behaves exactly
   *  as it would outside this component, since nothing overlays it. */
  to?: string;
  /** The link's content AND the row's accessible name. Plain text or a
   *  short inline node — this is what a screen reader announces for the
   *  row, so it must identify the row on its own (the prompt, the rule
   *  name, the flag key — never just "View"). */
  title: ReactNode;
  /** Optional leading slot: a disc, an avatar, an engine icon. */
  lead?: ReactNode;
  /** Optional line above the title: chips/badges (left) and a timestamp
   *  (auto-pushed right by .hub-row__time). */
  top?: ReactNode;
  /** Everything under the title: RowSnippet, RowSources, RowFacts, in any
   *  combination — this is what lets the recipe scale from one line to
   *  the full ResponseCard shape. */
  children?: ReactNode;
  /** Trailing controls — real buttons/links, rendered OUTSIDE the
   *  stretched anchor so their clicks never bubble into row navigation. */
  actions?: ReactNode;
  className?: string;
}

/**
 * One row of a queue. `<li>` inside `RowList`'s `<ul>`; not interactive
 * itself — no `tabindex`, no `role="button"`, no click handler on the `<li>`.
 *
 * With `to`: the row's click/hover/focus behaviour all comes from the single
 * `<Link>` this component renders around `title`, stretched to the row's
 * full box in CSS. Everything passed as `actions` is a plain DOM sibling of
 * that link; see the header comment above and primitives.css for why the
 * stacking order alone is enough to keep them independently clickable.
 *
 * Without `to`: `title` renders as plain text in the same spot, no `<a>` is
 * created, and nothing is stretched — this is the disclosure-row shape (spec
 * 235 §3.3). `actions`/`children` keep working exactly as they would in any
 * other component, since there is no overlay for them to out-rank.
 */
export function Row({ to, title, lead, top, children, actions, className, ...rest }: RowProps & Record<string, unknown>) {
  const titleEl = <span className="hub-row__title">{title}</span>;
  return (
    <li className={`hub-row${className ? ' ' + className : ''}`} {...rest}>
      {lead ? <div className="hub-row__lead">{lead}</div> : null}
      <div className="hub-row__body">
        {top ? <div className="hub-row__top">{top}</div> : null}
        {to ? (
          <Link to={to} className="hub-row__link">
            {titleEl}
          </Link>
        ) : (
          titleEl
        )}
        {children}
      </div>
      {actions ? <div className="hub-row__actions">{actions}</div> : null}
    </li>
  );
}

/** The clamped secondary paragraph — three lines, then an ellipsis (CSS
 *  line-clamp in primitives.css). Spreads `...rest` (spec 235 task 5 needed
 *  `data-testid="response-snippet"` to survive `ResponseCard`'s conversion
 *  without inventing a wrapper element just to hold it). */
export function RowSnippet({ children, ...rest }: { children: ReactNode } & Record<string, unknown>) {
  return <p className="hub-row__snippet" {...rest}>{children}</p>;
}

/** Wraps a row's `RowFact`s. Wraps at any count — a queue with two facts and
 *  one with four use the same component, just fewer/more children. Spreads
 *  `...rest` for the same reason as `RowSnippet` above. */
export function RowFacts({ children, ...rest }: { children: ReactNode } & Record<string, unknown>) {
  return <div className="hub-row__facts" {...rest}>{children}</div>;
}

/** One fact, its own slot — never a middle-dot-joined string. Monochrome by
 *  design: icon and text both take ink-muted, no per-value colour. A value
 *  that is genuinely a status belongs in `top` as a `Chip`, not here. */
export function RowFact({ icon, title: hint, children }: { icon?: string; title?: string; children: ReactNode }) {
  return (
    <span className="hub-row__fact" title={hint}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </span>
  );
}

export interface CitedDomain {
  domain: string;
  domain_type: string;
}

/** The favicon stack with overflow. Only the `<img>` itself keeps colour —
 *  the disc around it is monochrome (surface-sunken + hairline), replacing
 *  the per-domain-type coloured ring ResponseCard draws today; the domain
 *  type stays available in the `title` tooltip. */
export function RowSources({ domains, max = 5 }: { domains: CitedDomain[]; max?: number }) {
  if (!domains.length) return null;
  const shown = domains.slice(0, max);
  const overflow = domains.length - shown.length;
  return (
    <div className="hub-row__sources" data-testid="source-badges">
      {shown.map((d, i) => (
        <span key={`${d.domain}-${i}`} className="hub-row__source" title={`${d.domain} (${d.domain_type})`}>
          <img
            src={`https://www.google.com/s2/favicons?domain=${d.domain}&sz=32`}
            alt=""
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </span>
      ))}
      {overflow > 0 ? <span className="hub-row__source hub-row__source--overflow">+{overflow}</span> : null}
    </div>
  );
}
